<!--
  UserBadge.svelte — Compact inline user reference with avatar and name.

  Renders a small avatar (initials-based), the user's display name, and
  links to /Users/:id on click.  Designed for audit logs, dashboards,
  and anywhere a user reference appears inline.

  @prop {number}            userId    - The user's ID (used for the link).
  @prop {string | null}     firstName - First name (nullable).
  @prop {string | null}     lastName  - Last name (nullable).
  @prop {string}            email     - Email (fallback display).
  @prop {'xs' | 'sm'}       size      - Badge size variant.
-->
<script lang="ts">
	import { push } from '../abstract-spa-router';

	
	
	
	
	
	interface Props {
		/** The user's numeric ID — used for the link target. */
		userId: number;
		/** First name (nullable). */
		firstName?: string | null;
		/** Last name (nullable). */
		lastName?: string | null;
		/** Email address — used as fallback display text. */
		email?: string;
		/** Size variant controlling avatar and text dimensions. */
		size?: 'xs' | 'sm';
	}

	let {
		userId,
		firstName = null,
		lastName = null,
		email = '',
		size = 'sm'
	}: Props = $props();

	/** Computed display name: "First Last" or email fallback. */
	let displayName = $derived([firstName, lastName].filter(Boolean).join(' ') || email);

	/** One or two character initials for the avatar circle. */
	let initials =
		$derived(firstName && lastName
			? `${firstName[0]}${lastName[0]}`.toUpperCase()
			: (firstName || lastName || email || '?')[0].toUpperCase());

	/** Navigate to the user detail page. */
	function handleClick(e: MouseEvent): void {
		e.preventDefault();
		e.stopPropagation();
		push(`/Users/${userId}`);
	}

	const sizeClasses = {
		xs: { circle: 'w-5 h-5 text-[9px]', text: 'text-xs' },
		sm: { circle: 'w-6 h-6 text-[10px]', text: 'text-sm' }
	};
</script>

<a
	href="/Manage/Users/{userId}"
	class="group inline-flex items-center gap-1.5 text-gray-700 transition-colors hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400"
	onclick={handleClick}
	title={email}
>
	<span
		class="flex-shrink-0 {sizeClasses[size]
			.circle} flex items-center justify-center rounded-full bg-primary-100 font-semibold leading-none text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
	>
		{initials}
	</span>
	<span class="{sizeClasses[size].text} max-w-[160px] truncate font-medium group-hover:underline">
		{displayName}
	</span>
</a>
